import React, { useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, IconButton,
  Grid, CircularProgress, Alert, Tab, Tabs, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Person as PersonIcon,
  TextFields as TextIcon,
  FileUpload as CsvIcon,
  Check as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { useDropzone } from 'react-dropzone';

const TabPanel = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const ManualEntry = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManual = hasPermission('email_extractor_manual');
  const canCsv = hasPermission('email_extractor_csv');

  // Tab state
  const [tab, setTab] = useState(0);

  // Single email state
  const [singleEmail, setSingleEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [sourceNote, setSourceNote] = useState('');
  const [singleLoading, setSingleLoading] = useState(false);
  const [singleResult, setSingleResult] = useState(null);

  // Bulk text state
  const [bulkText, setBulkText] = useState('');
  const [bulkSourceNote, setBulkSourceNote] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // CSV state
  const [csvFile, setCsvFile] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvResult, setCsvResult] = useState(null);
  const [csvProgress, setCsvProgress] = useState(0);

  // Single email handler
  const handleAddSingle = async () => {
    if (!singleEmail.trim()) return;

    try {
      setSingleLoading(true);
      setSingleResult(null);

      const response = await api.post('/email-extractor/manual/single', {
        email: singleEmail.trim(),
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        source_note: sourceNote.trim() || null
      });

      setSingleResult({
        success: true,
        data: response.data.data,
        message: response.data.message
      });

      // Clear form on success
      setSingleEmail('');
      setFirstName('');
      setLastName('');
      setSourceNote('');
    } catch (error) {
      setSingleResult({
        success: false,
        message: error.response?.data?.message || 'Failed to add email'
      });
    } finally {
      setSingleLoading(false);
    }
  };

  // Bulk text handler
  const handleBulkAdd = async () => {
    if (!bulkText.trim()) return;

    try {
      setBulkLoading(true);
      setBulkResult(null);

      const response = await api.post('/email-extractor/manual/bulk-text', {
        text: bulkText,
        source_note: bulkSourceNote.trim() || null
      });

      setBulkResult({
        success: true,
        data: response.data.data,
        message: response.data.message
      });

      setBulkText('');
      setBulkSourceNote('');
    } catch (error) {
      setBulkResult({
        success: false,
        message: error.response?.data?.message || 'Failed to process emails'
      });
    } finally {
      setBulkLoading(false);
    }
  };

  // CSV upload handler
  const handleCsvUpload = async () => {
    if (!csvFile) return;

    try {
      setCsvLoading(true);
      setCsvResult(null);
      setCsvProgress(0);

      const formData = new FormData();
      formData.append('file', csvFile);

      const response = await api.post('/email-extractor/manual/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setCsvProgress(progress);
        }
      });

      setCsvResult({
        success: true,
        data: response.data.data,
        message: response.data.message
      });

      setCsvFile(null);
    } catch (error) {
      setCsvResult({
        success: false,
        message: error.response?.data?.message || 'Failed to import CSV'
      });
    } finally {
      setCsvLoading(false);
      setCsvProgress(0);
    }
  };

  // Dropzone
  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setCsvFile(acceptedFiles[0]);
      setCsvResult(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: csvLoading
  });

  // Count emails in bulk text
  const emailCount = bulkText
    ? bulkText.split(/[\n\r]+/).filter(line => {
        const trimmed = line.trim();
        return trimmed && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      }).length
    : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate('/email-extractor')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Import Emails
        </Typography>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tab} onChange={(e, newTab) => setTab(newTab)}>
            {canManual && (
              <Tab icon={<PersonIcon />} label="Single Email" iconPosition="start" />
            )}
            {canManual && (
              <Tab icon={<TextIcon />} label="Bulk Text" iconPosition="start" />
            )}
            {canCsv && (
              <Tab icon={<CsvIcon />} label="CSV Import" iconPosition="start" />
            )}
          </Tabs>
        </Box>

        {/* Single Email Tab */}
        {canManual && (
          <TabPanel value={tab} index={0}>
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Add a single email address
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={singleEmail}
                        onChange={(e) => setSingleEmail(e.target.value)}
                        placeholder="john.doe@company.com"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name (optional)"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name (optional)"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Doe"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Source Note (optional)"
                        value={sourceNote}
                        onChange={(e) => setSourceNote(e.target.value)}
                        placeholder="Where did you get this email?"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        variant="contained"
                        onClick={handleAddSingle}
                        disabled={!singleEmail.trim() || singleLoading}
                      >
                        {singleLoading ? <CircularProgress size={20} /> : 'Add Email'}
                      </Button>
                    </Grid>
                  </Grid>

                  {singleResult && (
                    <Alert
                      severity={singleResult.success ? 'success' : 'error'}
                      sx={{ mt: 2 }}
                      icon={singleResult.success ? <SuccessIcon /> : <ErrorIcon />}
                    >
                      {singleResult.message}
                      {singleResult.success && singleResult.data?.isNew === false && (
                        <Typography variant="body2">
                          This email already exists in the database.
                        </Typography>
                      )}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </TabPanel>
        )}

        {/* Bulk Text Tab */}
        {canManual && (
          <TabPanel value={tab} index={canManual ? 1 : 0}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Paste multiple email addresses (one per line)
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={10}
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                placeholder={`john@example.com
jane@company.com
bob.smith@domain.org`}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Chip
                  label={`${emailCount} valid email${emailCount !== 1 ? 's' : ''} detected`}
                  color={emailCount > 0 ? 'success' : 'default'}
                />
              </Box>
              <TextField
                fullWidth
                label="Source Note (optional)"
                value={bulkSourceNote}
                onChange={(e) => setBulkSourceNote(e.target.value)}
                placeholder="Where did you get these emails?"
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleBulkAdd}
                disabled={emailCount === 0 || bulkLoading}
              >
                {bulkLoading ? <CircularProgress size={20} /> : `Import ${emailCount} Emails`}
              </Button>

              {bulkResult && (
                <Alert
                  severity={bulkResult.success ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {bulkResult.message}
                  {bulkResult.success && bulkResult.data && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">Reference: {bulkResult.data.reference}</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        <Chip label={`${bulkResult.data.new} new`} color="success" size="small" />
                        <Chip label={`${bulkResult.data.duplicates} duplicates`} color="warning" size="small" />
                        <Chip label={`${bulkResult.data.excluded} excluded`} color="error" size="small" />
                      </Box>
                    </Box>
                  )}
                </Alert>
              )}
            </CardContent>
          </TabPanel>
        )}

        {/* CSV Import Tab */}
        {canCsv && (
          <TabPanel value={tab} index={canManual ? 2 : 0}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                Upload a CSV file with email addresses
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                The CSV file must have an "email" column. Optional columns: "first_name" or "firstname", "last_name" or "lastname".
              </Alert>

              <Box
                {...getRootProps()}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  cursor: csvLoading ? 'default' : 'pointer',
                  bgcolor: isDragActive ? 'primary.50' : 'background.default',
                  mb: 3,
                  transition: 'all 0.2s'
                }}
              >
                <input {...getInputProps()} />
                <CsvIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                {isDragActive ? (
                  <Typography>Drop the CSV file here...</Typography>
                ) : (
                  <Typography>
                    Drag & drop a CSV file here, or click to select
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Maximum file size: 10MB
                </Typography>
              </Box>

              {csvFile && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info">
                    Selected file: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
                  </Alert>
                  {csvLoading && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress variant="determinate" value={csvProgress} />
                      <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        Uploading... {csvProgress}%
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleCsvUpload}
                disabled={!csvFile || csvLoading}
              >
                {csvLoading ? <CircularProgress size={20} /> : 'Import CSV'}
              </Button>

              {csvResult && (
                <Alert
                  severity={csvResult.success ? 'success' : 'error'}
                  sx={{ mt: 2 }}
                >
                  {csvResult.message}
                  {csvResult.success && csvResult.data && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">Reference: {csvResult.data.reference}</Typography>
                      <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                          <TableBody>
                            <TableRow>
                              <TableCell>Total Rows</TableCell>
                              <TableCell align="right">{csvResult.data.total}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>New Emails</TableCell>
                              <TableCell align="right" sx={{ color: 'success.main' }}>
                                {csvResult.data.new}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Duplicates</TableCell>
                              <TableCell align="right" sx={{ color: 'warning.main' }}>
                                {csvResult.data.duplicates}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Excluded</TableCell>
                              <TableCell align="right" sx={{ color: 'error.main' }}>
                                {csvResult.data.excluded}
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Invalid</TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                                {csvResult.data.invalid}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Alert>
              )}
            </CardContent>
          </TabPanel>
        )}
      </Card>
    </Box>
  );
};

export default ManualEntry;
